<template>
  <section class="request-payment-step">
    <request-payment-filter
      :visible.sync="visibleFiltersModal"
      :current-filters="filters"
      :client-id="clientId"
      @update:currentFilters="applyFilters"
    />

    <div class="flex flex-column gap-1">
      <label>Client</label>
      <custom-select
        :multiple="false"
        :allow-clear="true"
        :value.sync="clientId"
        placeholder="Select client"
        :options="clients"
        :loading="clientsLoading"
        name="name"
        optionKey="id"
      />
    </div>

    <a-alert
      v-if="shiftsError"
      class="request-payment-alert"
      type="error"
      show-icon
      message="Shifts could not be loaded"
      description="Adjust the filters or try loading the shifts again."
    />
    <a-alert
      v-if="creationError"
      class="request-payment-alert"
      type="error"
      show-icon
      message="Payment request could not be created"
      :description="creationError"
    />
    <a-alert
      v-if="callbackManager && clientId"
      class="request-payment-alert"
      type="error"
      show-icon
      message="Payment request access required"
      :description="`You need administrator access for the selected client to create a payment request. Please contact ${callbackManager.first_name} ${callbackManager.last_name} for creating request: ${callbackManager.email}`"
    />

    <template v-if="staffingManager">
      <div class="selection-toolbar">
        <a-checkbox
          :checked="allEligibleSelected"
          :indeterminate="someEligibleSelected && !allEligibleSelected"
          :disabled="!eligibleShifts.length || shiftsLoading"
          @change="toggleAllEligible"
        >
          Select all eligible shifts
        </a-checkbox>

        <div class="flex gap-2">
          <a-button icon="filter" :disabled="!clientId" @click="visibleFiltersModal = true">Filters</a-button>
          <a-button icon="undo" :disabled="!clientId" @click="resetFilters">Reset Filters</a-button>
        </div>
      </div>

      <a-table
        size="small"
        :row-key="record => record.id"
        :columns="columns"
        :data-source="shifts"
        :loading="shiftsLoading"
        :pagination="pagination"
        :scroll="{ x: 820, y: 360 }"
        :locale="{ emptyText: clientId ? 'No shifts match these filters' : 'Select a client to load shifts' }"
        :row-selection="rowSelection"
        @change="handleTableChange"
        :custom-row="record => ({ on: { click: () => openShiftModal(record.id) } })"
      >
        <template slot="eligibility" slot-scope="text, record">
          <a-tag v-if="isEligible(record)" color="#10b981">Ready</a-tag>
          <a-tooltip v-else>
            <template #title>{{ shiftReason(record) }}</template>
            <a-tag color="#f59e0b">Unavailable</a-tag>
          </a-tooltip>
        </template>

        <template slot="footer">
          <span v-if="selectedShiftIds.length">{{ `Selected ${selectedShiftIds.length} shifts` }}</span>
        </template>
      </a-table></template
    >

    <request-payment-summary v-if="selectedShiftIds.length" :summary="summary" />
    <a-alert v-if="amountValidationError" type="error" show-icon :message="amountValidationError" />
  </section>
</template>

<script>
import moment from 'moment-timezone'
import { Alert, Button, Checkbox, Table, Tag, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import StripeNotificationMixin from '../../mixins/StripeNotificationMixin'
import RequestPaymentSummary from './request-payment-summary.vue'
import RequestPaymentFilter from './request-payment-filter.vue'
import {
  formatRequestPaymentMoney,
  getRequestPaymentShiftReason,
  getRequestPaymentShiftTotals,
  getRequestPaymentSummary,
  isRequestPaymentShiftEligible
} from '../../services/request-payment-utils'
import CustomSelect from '@/components/ui/custom-select.vue'

const { mapActions: paymentActions } = createNamespacedHelpers('payment')
const { mapActions: projectActions } = createNamespacedHelpers('project')
const { mapActions: shiftActions } = createNamespacedHelpers('shifts')
const { mapActions: profileActions } = createNamespacedHelpers('profile')

const MIN_REQUEST_AMOUNT = 400
const MAX_REQUEST_AMOUNT = 99999999

const createFilters = () => ({
  start_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
  end_date: moment().format('YYYY-MM-DD'),
  associates: undefined,
  departments: undefined,
  roles: undefined,
  payment_types: undefined,
  legal_entities: undefined,
  associate_statuses: undefined,
  pay_statuses: undefined,
  schedule_types: undefined,
  facility_project_ids: undefined
})

export default {
  mixins: [StripeNotificationMixin],
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-checkbox': Checkbox,
    'a-table': Table,
    'a-tag': Tag,
    'a-tooltip': Tooltip,
    'custom-select': CustomSelect,
    'request-payment-summary': RequestPaymentSummary,
    'request-payment-filter': RequestPaymentFilter
  },
  props: {
    initialClientId: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      clientId: this.initialClientId,
      clients: [],
      clientsLoading: false,
      filters: createFilters(),
      shifts: [],
      selectedShiftIds: [],
      shiftsLoading: false,
      shiftsError: '',
      creationError: '',
      isCreating: false,
      visibleFiltersModal: false,
      pagination: {
        current: 1,
        pageSize: 50,
        pageSizeOptions: ['10', '25', '50', '100'],
        showSizeChanger: true,
        showTotal: total => `Total ${total} shifts`
      },
      columns: [
        { title: 'Associate', customRender: (_, record) => this.associateName(record) },
        { title: 'Shift Date', customRender: (_, record) => this.formatDate(record.scheduled_start) },
        { title: 'Paid Hours', customRender: (_, record) => this.shiftTotals(record).hours.toFixed(2) },
        { title: 'Total', customRender: (_, record) => this.formatMoney(this.shiftTotals(record).amount) },
        { title: 'Availability', scopedSlots: { customRender: 'eligibility' } }
      ],
      requestGroup: `request-payment-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      staffingManager: null,
      callbackManager: null,
      loadingManager: false
    }
  },
  computed: {
    currentAssociate() {
      return this.$store.state.applicationState.associate
    },
    eligibleShifts() {
      return this.shifts.filter(this.isEligible)
    },
    selectedShifts() {
      const ids = new Set(this.selectedShiftIds)
      return this.shifts.filter(shift => ids.has(shift.id) && this.isEligible(shift))
    },
    summary() {
      return getRequestPaymentSummary(this.selectedShifts)
    },
    amountValidationError() {
      if (!this.selectedShifts.length) return ''
      if (!Number.isSafeInteger(this.summary.amount)) return 'The selected shifts have an invalid total amount.'
      if (this.summary.amount < MIN_REQUEST_AMOUNT) return 'The payment request total must be at least $4.00.'
      if (this.summary.amount > MAX_REQUEST_AMOUNT) return 'The payment request total cannot exceed $999,999.99.'
      return ''
    },
    canCreate() {
      return !this.isCreating && this.staffingManager && this.selectedShifts.length > 0 && !this.amountValidationError
    },
    stepState() {
      return { canContinue: this.canCreate, loading: this.isCreating, label: 'Continue' }
    },
    selectedEligibleCount() {
      const selected = new Set(this.selectedShiftIds)
      return this.eligibleShifts.filter(shift => selected.has(shift.id)).length
    },
    allEligibleSelected() {
      return this.eligibleShifts.length > 0 && this.selectedEligibleCount === this.eligibleShifts.length
    },
    someEligibleSelected() {
      return this.selectedEligibleCount > 0
    },
    rowSelection() {
      return {
        selectedRowKeys: this.selectedShiftIds,
        onChange: this.selectShifts,
        getCheckboxProps: record => ({ props: { disabled: !this.isEligible(record) } })
      }
    }
  },
  watch: {
    stepState: {
      handler(state) {
        this.$emit('state-change', state)
      },
      immediate: true
    },
    clientId: {
      handler() {
        this.changeClient()
      },
      immediate: true
    },
    shifts() {
      this.pagination.current = 1
    }
  },
  mounted() {
    this.initialize()
  },
  methods: {
    ...paymentActions(['createPaymentRequest']),
    ...projectActions(['getClients', 'getClientsManagement']),
    ...shiftActions(['getShifts']),
    ...profileActions(['getAssociateProfiles']),

    formatMoney: formatRequestPaymentMoney,
    shiftTotals: getRequestPaymentShiftTotals,
    shiftReason: getRequestPaymentShiftReason,
    isEligible: isRequestPaymentShiftEligible,

    async initialize() {
      await this.loadClients()
      if (this.clientId) await this.fetchAssociateProfiles()
    },

    async loadClients() {
      this.clientsLoading = true
      try {
        const data = await this.getClients({ page: 1, size: 100000, fields: '{id,name}' })
        this.clients = data.items || []
      } catch (error) {
        this.showErrorNotification(error, 'Clients could not be loaded. Try again.')
      } finally {
        this.clientsLoading = false
      }
    },

    changeClient() {
      this.clearShiftSelection()
      this.staffingManager = null
      this.callbackManager = null

      if (this.clientId) {
        this.fetchAssociateProfiles()
      }
    },

    applyFilters(filters) {
      this.filters = filters
      this.clearShiftSelection()
      this.loadShifts()
    },

    resetFilters() {
      this.applyFilters(createFilters())
    },

    clearShiftSelection() {
      this.selectedShiftIds = []
      this.creationError = ''
    },

    selectShifts(ids) {
      const eligibleIds = new Set(this.eligibleShifts.map(shift => shift.id))
      this.selectedShiftIds = ids.filter(id => eligibleIds.has(id))
    },

    handleTableChange(pagination) {
      this.pagination = { ...this.pagination, current: pagination.current, pageSize: pagination.pageSize }
    },

    toggleAllEligible(event) {
      this.selectShifts(event.target.checked ? this.eligibleShifts.map(shift => shift.id) : [])
    },

    associateName(shift) {
      const associate = shift?.external_payment_info?.associate || {}
      return [associate.first_name, associate.last_name].filter(Boolean).join(' ') || `Associate #${associate.id}`
    },

    formatDate(date) {
      return date ? moment(date).format(this.$store.state.applicationState.dateFormat) : '-'
    },

    openShiftModal(openShift) {
      const href = this.$router.resolve({ query: { ...this.$route.query, openShift } }).href
      window.open(window.location.origin + href, '_blank')
    },

    buildShiftFilters() {
      return {
        ...this.filters,
        facility_project_ids: [this.clientId],
        staffing: 'external',
        shift_statuses: ['completed']
      }
    },

    async fetchAssociateProfiles() {
      if (!this.clientId || !this.currentAssociate?.associate_number) return

      this.loadingManager = true
      try {
        const payload = {
          associate_number: this.currentAssociate.associate_number,
          with_disabled: false,
          project_id: this.clientId
        }

        const [data, management] = await Promise.all([
          this.getAssociateProfiles(payload),
          this.getClientsManagement({ facility_project_id: this.clientId })
        ])

        this.staffingManager = (data.associates || []).find(
          item =>
            item.permissions?.some(permission => ['validate_payments', 'process_payments'].includes(permission.name)) &&
            management.find(manager => manager.id === item.id)
        )

        if (!this.staffingManager && management.length) {
          this.callbackManager = management.find(item =>
            item.permissions.some(permission => ['validate_payments', 'process_payments'].includes(permission.name))
          )
        } else {
          await this.loadShifts()
        }
      } catch (error) {
        this.showNotification(
          'error',
          'Payment request access could not be checked',
          typeof error === 'string' ? error : error?.message
        )
      } finally {
        this.loadingManager = false
      }
    },

    async loadShifts() {
      if (!this.clientId) return
      this.shiftsLoading = true
      this.shiftsError = ''
      try {
        const data = await this.getShifts({
          page: 1,
          size: 100000,
          filter: this.buildShiftFilters(),
          fields:
            '{id,status,scheduled_start,scheduled_end,external_payment_info{associate,legal_entity,not_ready_to_pay_flags,ready_for_pay_status,payment_status,calculations,extra,rate}}',
          requestGroup: this.requestGroup
        })
        this.shifts = data.items || []
      } catch (error) {
        if (error === 'canceled') return
        this.shiftsError = typeof error === 'string' ? error : error?.message
        this.showErrorNotification(error, 'Shifts could not be loaded. Try again.')
      } finally {
        this.shiftsLoading = false
      }
    },

    async createRequest() {
      if (!this.canCreate) return
      this.isCreating = true
      this.creationError = ''
      try {
        const requestId = await this.createPaymentRequest({
          associate_id: this.staffingManager.id,
          shift_ids: this.selectedShiftIds,
          facility_project_id: this.clientId
        })
        this.$emit('created', requestId)
      } catch (error) {
        this.creationError = typeof error === 'string' ? error : error?.message
      } finally {
        this.isCreating = false
      }
    }
  }
}
</script>

<style scoped>
.request-payment-step {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.selection-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: var(--text-secondary);
}
.request-payment-alert {
  margin: 0;
}
</style>
